import {
  Button,
  Icon,
  LockHollowSVG,
  PrintSVG,
  useModal,
} from "@newsela/angelou";

import {
  withTrackEvent,
  objectTypes,
  actionNames,
  actionPrefixes,
} from "@newsela/snitch";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import { Assignment } from "@/types/models/Assignment.interface";
import { Attachment, Quiz } from "@/types/models/ContentAttachment.interface";
import ContentLevel from "@/types/models/ContentLevel.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import ActionsDropdown from "@/view/ActionsDropdown";
import assessmentTypes from "@/view/constants/assessmentTypes";
import PrintButton from "@/view/PrintButton";
import shouldDisableArticlePrint from "@/view/utils/shouldDisableArticlePrint";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import useAttachmentsContext from "@/view/utils/useAttachmentsContext";

import { getPrintModalOptions } from "./getPrintModalOptions";
import { getPrintOptions } from "./getPrintOptions";
import PopOutContents from "./PopOutContents";
import PrintModal from "./PrintModal";
import {
  dropdownCss,
  dropdownButtonCss,
  lockIconCss,
  printIconCss,
} from "./PrintSelector.style";

interface PrintSelectorInterface {
  contentId: string;
  currentLevel: ContentLevel;
  isLiteContent: boolean;
  isMobile?: boolean | undefined;
  quizOmissionReason?: string | null;
  userProperties: UserProperties;
}

interface PrintOptionsButtonInterface {
  contentId: string;
  buttonProps?: Record<string, unknown>;
  currentLevel: ContentLevel;
  isLiteContent: boolean;
  isMobile?: boolean | undefined;
  quizOmissionReason?: string | null;
  userProperties: UserProperties;
  setIsAssignModalActive: (a: boolean) => void;
}

const PrintSelector = ({
  contentId,
  currentLevel,
  isLiteContent,
  isMobile,
  quizOmissionReason,
  userProperties,
}: PrintSelectorInterface | PrintOptionsButtonInterface) => {
  const ButtonWithTrackEvent = withTrackEvent(
    Button,
  ) as React.ForwardRefExoticComponent<
    React.ComponentProps<typeof Button> & React.RefAttributes<HTMLButtonElement>
  >;

  const { updatedAssignment } = useAssignmentReducerContext() as {
    updatedAssignment: Assignment;
  };

  const assignmentId = updatedAssignment?.content_id;

  const {
    isStudent,
    isTeacher,
    isAuthorizedPrincipal,
    isAuthorizedDistrictAdmin,
  } = userProperties;
  const { WRITE_PROMPT, QUIZ, POWER_WORDS_ACTIVITY } = assessmentTypes;

  const baseUrl = `/content/${currentLevel.id}/print/?format=pdf&content=`;

  const isArticlePrintDisabled = shouldDisableArticlePrint(
    userProperties,
    isLiteContent,
  );

  const { attachments } = useAttachmentsContext();
  const quizAttachments = attachments?.filter(
    (attachment: Attachment) => attachment.content_type === QUIZ.type,
  );
  const contentHasPrintableQuiz = quizAttachments?.some(
    (quiz: Quiz) => quiz?.subject?.data?.subject === "ELA",
  );
  const contentHasWritePrompt = attachments?.some(
    (attachment: Attachment) => attachment.content_type === WRITE_PROMPT.type,
  );
  const contentHasPowerWords = attachments?.some(
    (attachment: Attachment) =>
      attachment.content_type === POWER_WORDS_ACTIVITY.type,
  );
  const isAdmin = isAuthorizedPrincipal || isAuthorizedDistrictAdmin;

  const [printModalVariation] = useGetUserExperimentSegment("print_modal");
  const hasPrintModalFeature =
    printModalVariation === "on" &&
    !isArticlePrintDisabled &&
    (isTeacher || isAdmin);
  const [modalProps, setModalActive] = useModal(false);

  const printOptions = hasPrintModalFeature
    ? getPrintModalOptions(
        contentHasPrintableQuiz,
        contentHasWritePrompt,
        contentHasPowerWords,
      )
    : getPrintOptions({
        isStudent,
        isTeacher,
        isAdmin,
        contentHasWritePrompt,
        isStudentPreviewOn: false,
        contentHasPrintableQuiz,
      });

  const sharedEventProperties = {
    objectType: objectTypes.content,
    actionName: actionNames.print,
    streamProperties: {
      content_type: "Article",
      description: `Open print options${hasPrintModalFeature ? " in modal" : ""}`,
      content_id: currentLevel?.id,
      article_header_content_id: currentLevel?.header_id,
      article_story_content_id: contentId,
    },
  };

  const printMenuEventProperties = {
    ...sharedEventProperties,
    actionPrefix: actionPrefixes.initiate,
  };

  const displaySingleOption = printOptions?.length === 1 || quizOmissionReason;

  const popOutContents = () => {
    return (
      <PopOutContents
        assignmentId={assignmentId}
        currentLevelId={currentLevel.id}
        baseUrl={baseUrl}
        eventProperties={sharedEventProperties}
        isMobile={isMobile}
        isPrintDisabled={isArticlePrintDisabled}
        printOptions={printOptions}
      />
    );
  };

  if (!printOptions) {
    return null;
  }

  if (displaySingleOption) {
    if (!currentLevel) {
      return null;
    }

    return (
      <PrintButton
        contentId={contentId}
        currentLevelId={currentLevel.id}
        isArticlePrintDisabled={isArticlePrintDisabled}
        currentLevelHeaderId={currentLevel.header_id}
        showIconOnly={isMobile}
      />
    );
  }

  if (hasPrintModalFeature) {
    return (
      <>
        <ButtonWithTrackEvent
          __cssFor={{ root: dropdownButtonCss(isMobile) }}
          ariaProps={{ "aria-label": "View printing options in modal." }}
          dataProps={{ "data-qa-selector": "initiate-print-modal" }}
          flavor={Button.flavors.muted}
          icon={{
            SvgComponent: PrintSVG,
            alignment: Button.iconAlignments.left,
          }}
          onClick={() => {
            setModalActive(true);
          }}
          // TODO - should this be different for modal button?
          {...printMenuEventProperties}
        >
          {isMobile ? null : "Print"}
        </ButtonWithTrackEvent>
        {modalProps.isActive && (
          <PrintModal
            assignmentId={assignmentId}
            currentLevelId={currentLevel.id}
            baseUrl={baseUrl}
            eventProperties={sharedEventProperties}
            printOptions={printOptions}
            PrintModalProps={modalProps}
          />
        )}
      </>
    );
  }

  return (
    <ActionsDropdown
      // @ts-expect-error remove if ActionsDropdown gets converted to TS
      horizontalOffset="-4rem"
      ariaProps={{
        "aria-label": isArticlePrintDisabled
          ? "View unavailable printing options."
          : "View printing options.",
      }}
      buttonCss={dropdownButtonCss(isMobile)}
      buttonFlavor={Button.flavors.muted}
      buttonText={
        isMobile ? (
          <Icon
            __cssFor={{ root: printIconCss }}
            isDecorative={false}
            title="print"
            size={Button.sizes.md}
            SvgComponent={PrintSVG}
          />
        ) : isArticlePrintDisabled ? (
          <>
            <Icon
              isDecorative={false}
              title="locked"
              __cssFor={{ root: lockIconCss(isArticlePrintDisabled, true) }}
              size={Button.sizes.md}
              SvgComponent={LockHollowSVG}
            />
            Print
          </>
        ) : (
          "Print"
        )
      }
      dataProps={{ "data-qa-selector": "initiate-print" }}
      dropdownCss={dropdownCss}
      eventProperties={printMenuEventProperties}
      horizontalPopOutPosition={isMobile ? "RIGHT" : "LEFT"}
      id={`print-selector-${currentLevel.id}`}
      isArticlePrintDisabled={isArticlePrintDisabled}
      renderDropdownItems={popOutContents}
    />
  );
};

export default PrintSelector;
